import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { change, Field, Form, getFormValues, InjectedFormProps } from 'redux-form';
import { Select } from 'antd';
import { bindActionCreators, Dispatch } from 'redux';

import { RootState } from 'app/redux';
import { reduxFormWrapper } from 'app/components/common/form';
import { Typography } from 'app/components/domain/typography';
import { FormCheckbox, FormDatePicker, FormInput, FormSelect } from 'app/components/common/inputs';
import { SubmitButton } from 'app/components/common/buttons';
import { i18n } from 'app/i18n';
import { validators } from 'app/validation';
import { RescheduleTechnicianFormModel } from 'app/page/orders/reschedule-technician/reschedule-technician-form-model';
import { RESCHEDULE_TECHNICIAN_FORM_FIELDS } from 'app/page/orders/reschedule-technician/reschedule-technician-form-fields';

import { RescheduleTechnicianMapper } from './reschedule-technician-mapper';

import styles from './reschedule-technician-form.module.scss';

interface OwnProps {
    onCalendarDayVisit: (month: moment.Moment) => void;
}

interface StateProps {
    formValues: Partial<RescheduleTechnicianFormModel>;
    availableTime: OrderTrackerBackend.DayTimeSlots[];
}

interface DispatchProps {
    actions: {
        changeFormField: typeof change;
    };
}

type Props = OwnProps & StateProps & DispatchProps & InjectedFormProps<RescheduleTechnicianFormModel, OwnProps>;

const { Option } = Select;

const requiredValidation = validators.required();
const minNamesLengthValidation = validators.minLength(2);
const maxNamesLengthValidation = validators.maxLength(64);
const phoneNumberWithCountryCallCodeValidation = validators.phoneNumberWithCountryCallCodeValidation();

const renderInstallationAndActivationTimeOptions = (
    availableTime: OrderTrackerBackend.DayTimeSlots[],
    formValues: Partial<RescheduleTechnicianFormModel>,
): React.ReactNode => {
    const day: moment.Moment | undefined = moment(formValues.installationDate);
    const dayTimeSlots = day ? RescheduleTechnicianMapper.findDayTimeSlots(availableTime, day) : [];

    return [
        <Option key={0} disabled={true}>
            <Typography variant="body1" component="strong">
                {i18n.rescheduleTechnician.form.field.installationHour.defaultValue}
            </Typography>
        </Option>,
        ...dayTimeSlots.map((timeSlot) => (
            <Option key={timeSlot} value={timeSlot}>
                {i18n.rescheduleTechnician.form.timeSlot[timeSlot]}
            </Option>
        )),
    ];
};

const RescheduleTechnicianFormComponent: React.FC<Props> = (props: Props): React.ReactElement<Props> => {
    const {
        form,
        submitting,
        handleSubmit,
        availableTime,
        formValues,
        onCalendarDayVisit,
        actions,
        initialValues: { installationDate },
    } = props;

    const disabledDate = React.useCallback(
        (date: moment.Moment) => {
            // Infonova requires additional access to reschedule the appointment to an earlier date
            if (date.isBefore(installationDate)) {
                return true;
            }

            onCalendarDayVisit(date);
            return RescheduleTechnicianMapper.findDayTimeSlots(availableTime, date).length === 0;
        },
        [availableTime, installationDate, onCalendarDayVisit],
    );

    const handleDateChange = React.useCallback(
        (dateAny: any) => {
            const date: string = dateAny;

            if (!date) {
                return;
            }
            const timeSlots = RescheduleTechnicianMapper.findDayTimeSlots(availableTime, moment(date));

            if (!timeSlots.some((slot) => slot === formValues.installationHour)) {
                actions.changeFormField(form, 'installationHour', timeSlots[0]);
            }
        },
        [actions, availableTime, form, formValues],
    );

    return (
        <Form className={styles.rescheduleDetailsForm} name={form} onSubmit={handleSubmit}>
            <Typography className={styles.title} variant="heading">
                {i18n.rescheduleTechnician.form.title.connectingAndActivating}
            </Typography>
            <Typography className={styles.installationDateAndTime} variant="subtitle3">
                {i18n.rescheduleTechnician.form.installationTimeAndDate}
            </Typography>
            <Field
                label={i18n.rescheduleTechnician.form.field.installationDate.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.installationDate}
                component={FormDatePicker}
                required={true}
                validate={requiredValidation}
                disabledDate={disabledDate}
                onChange={handleDateChange}
            />
            <Field
                label={i18n.rescheduleTechnician.form.field.installationHour.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.installationHour}
                component={FormSelect}
                required={true}
                validate={requiredValidation}
                children={renderInstallationAndActivationTimeOptions(availableTime, formValues)}
            />
            <Typography className={styles.contactInformationTitle} variant="subtitle3">
                {i18n.rescheduleTechnician.form.title.contactInformation}
            </Typography>
            <Field
                label={i18n.rescheduleTechnician.form.field.contactName.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.contactName}
                placeholder={i18n.rescheduleTechnician.form.field.contactName.placeholder}
                component={FormInput}
                required={true}
                validate={[requiredValidation, minNamesLengthValidation, maxNamesLengthValidation]}
            />
            <Field
                label={i18n.rescheduleTechnician.form.field.phoneNumber.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.phoneNumber}
                placeholder={i18n.rescheduleTechnician.form.field.phoneNumber.placeholder}
                component={FormInput}
                required={true}
                validate={[requiredValidation, phoneNumberWithCountryCallCodeValidation]}
                className={styles.phoneNumber}
            />
            <Field
                label={i18n.rescheduleTechnician.form.field.technicianCanContactMe.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.technicianCanContactMe}
                required={true}
                component={FormCheckbox}
                validate={requiredValidation}
            >
                {i18n.rescheduleTechnician.form.field.technicianCanContactMe.content}
            </Field>
            <Field
                label={i18n.common.form.field.rightOfWithdrawalIsAgreed.label}
                name={RESCHEDULE_TECHNICIAN_FORM_FIELDS.rightOfWithdrawalIsAgreed}
                component={FormCheckbox}
                tooltip={{
                    title: i18n.common.form.field.rightOfWithdrawalIsAgreed.label,
                    text: i18n.common.form.field.rightOfWithdrawalIsAgreed.tooltip.text,
                }}
                required={true}
                validate={requiredValidation}
            >
                {i18n.common.form.field.rightOfWithdrawalIsAgreed.content}
            </Field>
            <SubmitButton submitting={submitting} size="small" className={styles.button}>
                {i18n.rescheduleTechnician.form.button.save}
            </SubmitButton>
        </Form>
    );
};

const mapStateToProps = (
    state: RootState,
    ownProps: OwnProps & InjectedFormProps<RescheduleTechnicianFormModel, OwnProps>,
): StateProps => {
    const { form } = ownProps;

    const {
        orders: { availableTime },
    } = state;

    return {
        formValues: getFormValues(form)(state),
        availableTime,
    };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    actions: {
        changeFormField: bindActionCreators(change, dispatch),
    },
});

const RescheduleTechnicianFormWithValues = connect(
    mapStateToProps,
    mapDispatchToProps,
)(RescheduleTechnicianFormComponent);
const RescheduleTechnicianForm = reduxFormWrapper<RescheduleTechnicianFormModel, OwnProps>({})(
    RescheduleTechnicianFormWithValues,
);

export { RescheduleTechnicianForm };
